import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/tailwind.util';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { useAiBuilderQuestionMutationLock } from '../hooks/useAiBuilderQuestionMutationLock';
import { useViewportSize } from '../hooks/useViewportSize';
import { useAiBuilderChatActions } from '../store';
import { AiBuilderGenerationLockOverlay, getAiBuilderGenerationOverlayCopy, preventAiBuilderShellScroll } from './AiBuilderGenerationLockOverlay';
import { AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION, AI_BUILDER_SHELL_TRANSITION_DURATION, AI_BUILDER_SHELL_TRANSITION_EASE, type AiBuilderPageSurfaceState, getAiBuilderPageRect, getAiBuilderPageSurfaceAnimation, getAiBuilderPageSurfaceRect } from './AiBuilderShellTransition';
const GENERATION_VISUAL_DELAY_MS = 40;

/**
 * 빌드 페이지 표면을 "전체 페이지 ↔ 떠 있는 카드(shell)"로 모프시키는 프레임.
 * 문제 생성/적용 중에는 표면 전체를 잠그는 오버레이를 함께 렌더한다.
 */
export const AiBuilderPageMorphFrame = ({
  children,
  state
}: {
  children: ReactNode;
  state: AiBuilderPageSurfaceState;
}) => {
  const viewportSize = useViewportSize();
  const copy = useAiBuilderChatCopy();
  const {
    cancelGeneration
  } = useAiBuilderChatActions();
  const {
    isQuestionMutationRunning,
    overlayMode
  } = useAiBuilderQuestionMutationLock();
  const isGeneratingQuizzes = state !== 'page' && isQuestionMutationRunning;
  const isGenerationVisualVisible = useDeferredGenerationVisualState(isGeneratingQuizzes);
  const setShellContentRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    if (isGeneratingQuizzes) {
      node.setAttribute('inert', '');
      return;
    }
    node.removeAttribute('inert');
  }, [isGeneratingQuizzes]);
  return <motion.div data-ai-builder-page-surface-state={state} data-ai-builder-page-generation-lock={isGeneratingQuizzes ? 'true' : undefined} data-ai-builder-page-generation-visual={isGeneratingQuizzes ? isGenerationVisualVisible ? 'visible' : 'hidden' : undefined} className={cn(state === 'page' ? 'relative' : 'fixed isolate z-[60] flex flex-col overflow-hidden border border-opacity bg-neutral')} initial={false} animate={getAiBuilderPageSurfaceAnimation({
    pageRect: getAiBuilderPageRect(viewportSize),
    shellRect: getAiBuilderPageSurfaceRect(viewportSize),
    state
  })} transition={{
    duration: state === 'closing' ? AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION : AI_BUILDER_SHELL_TRANSITION_DURATION,
    ease: AI_BUILDER_SHELL_TRANSITION_EASE
  }} onWheelCapture={isGeneratingQuizzes ? preventAiBuilderShellScroll : undefined} onTouchMoveCapture={isGeneratingQuizzes ? preventAiBuilderShellScroll : undefined} style={{
    pointerEvents: 'auto',
    willChange: 'top, left, width, height, border-radius'
  }} data-sentry-element="unknown" data-sentry-component="AiBuilderPageMorphFrame" data-sentry-source-file="AiBuilderPageMorphFrame.tsx">
      <div ref={setShellContentRef} className="contents" data-ai-builder-shell-locked={isGeneratingQuizzes ? 'true' : undefined} data-ai-builder-shell-content>
        {children}
      </div>
      {isGeneratingQuizzes && <AiBuilderGenerationLockOverlay visualVisible={isGenerationVisualVisible}>
          <AiBuilderGenerationLockOverlay.StatusToolbar onCancel={cancelGeneration}>
            {getAiBuilderGenerationOverlayCopy(overlayMode, copy)}
          </AiBuilderGenerationLockOverlay.StatusToolbar>
        </AiBuilderGenerationLockOverlay>}
    </motion.div>;
};

/**
 * 잠금이 켜진 다음 프레임에 glow/워시를 보이게 해 CSS opacity 트랜지션이
 * 실제로 재생되도록 한다(마운트와 동시에 visible이면 페이드 인이 생략된다).
 */
const useDeferredGenerationVisualState = (isGeneratingQuizzes: boolean) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (!isGeneratingQuizzes) {
      setIsVisible(false);
      return;
    }
    setIsVisible(false);
    const timerId = window.setTimeout(() => setIsVisible(true), GENERATION_VISUAL_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, [isGeneratingQuizzes]);
  return isGeneratingQuizzes && isVisible;
};